import { useCallback, useEffect, useRef } from "react";
import debounce from "lodash/debounce";

import { AUTOSAVE_DEBOUNCE_MS } from "../constants";

interface UseAutosaveOptions {
  /** The current text content for the active carrier. */
  content: string;
  /** The carrier ID currently being edited. */
  carrierId: string;
  /** Whether the current carrier has unsaved changes. */
  isUnsaved: boolean;
  /** Whether the current content is within the character limit. */
  isWithinLimit: boolean;
  /** Trigger a save for the given carrier and content. */
  onSave: (carrierId: string, content: string) => void;
}

/**
 * Debounced autosave that fires after the user stops typing.
 *
 * Also exposes `flushSave()` for imperative triggers (tab switch,
 * drawer close) and cleans up on unmount.
 */
export const useAutosave = ({
  content,
  carrierId,
  isUnsaved,
  isWithinLimit,
  onSave,
}: UseAutosaveOptions): { flushSave: () => void } => {
  const contentRef = useRef(content);
  const carrierIdRef = useRef(carrierId);
  const isUnsavedRef = useRef(isUnsaved);
  const isWithinLimitRef = useRef(isWithinLimit);
  const onSaveRef = useRef(onSave);

  contentRef.current = content;
  carrierIdRef.current = carrierId;
  isUnsavedRef.current = isUnsaved;
  isWithinLimitRef.current = isWithinLimit;
  onSaveRef.current = onSave;

  const debouncedSave = useRef(
    debounce(() => {
      if (isUnsavedRef.current && isWithinLimitRef.current) {
        onSaveRef.current(carrierIdRef.current, contentRef.current);
      }
    }, AUTOSAVE_DEBOUNCE_MS),
  ).current;

  // Trigger debounced save on content changes
  useEffect(() => {
    if (isUnsaved) {
      debouncedSave();
    }
    return () => {
      debouncedSave.cancel();
    };
  }, [content, isUnsaved, debouncedSave]);

  const flushSave = useCallback(() => {
    debouncedSave.cancel();
    if (isUnsavedRef.current) {
      onSaveRef.current(carrierIdRef.current, contentRef.current);
    }
  }, [debouncedSave]);

  // Flush on unmount
  useEffect(
    () => () => {
      debouncedSave.cancel();
    },
    [debouncedSave],
  );

  return { flushSave };
};
